import {
  type ConnectorStandard,
  type CurrentType,
  OPERATORS,
  type OperatorBrand,
  OTHER_OPERATOR_SLUG,
  type PaymentMethod,
  type PointAvailability,
  sortConnectorStandards,
  type VoltageClass,
} from '@ev/shared'
import type { Filters } from '../../state/filters'
import { FILTER_FACETS } from '../../state/filters'

/**
 * Which values each facet offers, and how the panel turns them into filters.
 *
 * Every option here is a value from a closed union in `@ev/shared`, so the URL
 * codec accepts anything the panel can produce and a value that leaves the
 * registry becomes a compile error rather than a dead checkbox.
 */

/** Operator rows, registry order, `other` last. */
export const OPERATOR_OPTIONS: readonly OperatorBrand[] = [
  ...OPERATORS.filter((operator) => operator.slug !== OTHER_OPERATOR_SLUG),
  ...OPERATORS.filter((operator) => operator.slug === OTHER_OPERATOR_SLUG),
]

/**
 * The plugs a driver actually chooses between. The registry knows twenty
 * standards; nineteen of them are museum pieces or China-only in Poland.
 */
export const CONNECTOR_OPTIONS: readonly ConnectorStandard[] = sortConnectorStandards([
  'CCS2',
  'CHADEMO',
  'TYPE2',
  'TESLA',
  'DOMESTIC',
])

/** `unknown` is a data-quality state, not something anyone filters *for*. */
export const CURRENT_TYPE_OPTIONS: readonly CurrentType[] = ['AC', 'DC']

/**
 * Coarse capability thresholds - what the charger can supply, never what the
 * car carries. The registry splits DC at 500 V and stops there, so the panel
 * offers five buckets rather than separating an 800 V class from a 1000 V one;
 * anything finer than this is the detail panel's job, from the derived range.
 */
export const VOLTAGE_CLASS_OPTIONS: readonly VoltageClass[] = [
  'ac-230',
  'ac-400',
  'dc-400',
  'dc-800',
  'unknown',
]

/** Availability is one checkbox: sites with at least one free point. */
export const FREE_POINT_AVAILABILITY: PointAvailability = 'free'

/** Payment is one checkbox: the site takes a contactless bank card. */
export const CONTACTLESS_PAYMENT_METHOD: PaymentMethod = 'payment-card'

/**
 * Slider stops, in kW. Discrete rather than continuous because the underlying
 * population is discrete: Polish sites cluster on 3.7 / 7.4 / 11 / 22 kW AC and
 * 50 / 150 / 350 kW DC, and a continuous slider mostly lands between clusters.
 *
 * Above 150 the stops run in 50 kW steps, because that is where the fleet has
 * since spread out: 200 and 300 kW cabinets are common enough that "at least
 * 200" should not have to be asked as 150 - which lets in cabinets that cannot
 * serve it - or as 250, which hides the ones that can.
 */
export const POWER_STOPS: readonly number[] = [
  0, 3.7, 7.4, 11, 22, 50, 75, 100, 150, 200, 250, 300, 350,
]

/** Slider index -> the value to store. Stop 0 means "no constraint". */
export const powerAtStop = (index: number): number | null => {
  const stop = POWER_STOPS[index]
  return stop === undefined || stop === 0 ? null : stop
}

/**
 * Stored value -> slider index, snapping to the nearest stop.
 *
 * A hand-edited URL can carry any number, so the slider shows the closest stop
 * while the readout keeps reporting the real value. Nothing rewrites the stored
 * number until the user actually moves the slider.
 */
export const stopForPower = (powerKw: number | null): number => {
  if (powerKw === null) return 0
  let best = 0
  POWER_STOPS.forEach((stop, index) => {
    if (Math.abs(stop - powerKw) < Math.abs((POWER_STOPS[best] ?? 0) - powerKw)) best = index
  })
  return best
}

/**
 * Typed text -> the value to store, or `undefined` when the text is not a power.
 *
 * The stops cover the fleet, not every charger in it, so the panel also takes a
 * number typed by hand. Whole kilowatts only and never negative: ratings are
 * whole above the AC stops, and the two forms that are neither - the empty
 * field and a literal 0 - both mean "no constraint", exactly as stop 0 does.
 *
 * `undefined` is "not a number yet", which is what a field mid-keystroke holds:
 * `2.` and `-` are on the way to something, so they leave the stored filter
 * where it is rather than clearing it.
 */
export const parsePowerInput = (raw: string): number | null | undefined => {
  const text = raw.trim()
  if (text === '') return null
  if (!/^\d+$/.test(text)) return undefined
  const powerKw = Number(text)
  if (!Number.isSafeInteger(powerKw)) return undefined
  return powerKw === 0 ? null : powerKw
}

/** How many constraints the user has set, for the active-filter summary. */
export const countActiveFilters = (filters: Filters): number =>
  FILTER_FACETS.reduce((total, facet) => total + filters[facet].length, 0) +
  (filters.minPowerKw === null ? 0 : 1)
